import { Icon, Button, Card, EmptyState, AutoTable, SelectButton, Modal, DropdownItem, TableCellDropdown, DropdownLine, Message, ToastContext, Toggle } from '@weezevent/nacre';
import React from 'react';
import Style from './editCrossSelling.module.css';
import i18n from '../../../i18n/i18n';
import VariantModel from '../../../models/variantModel';
import VariantModal from './variantModal';
import QuotasProductModel from '../../../models/quotasProductModel';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../components/loadingCard';
import { fetcher } from '@weezevent/weezjs-core';
import { getProductsLanguages, setTranslate } from './list/translationUtils';

export default function VariantList({
    organization,
    variants,
    setVariants,
    loadingVariants,
    syncVariants,
    productId,
    product,
    syncProduct,
    modalForm,
    setForm,
    setFormObject,
    resetmodal,
    defaultclickErrors
}) {
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();

    const [submitting, setSubmitting] = React.useState(false);
    const [openModal, setOpenModal] = React.useState(false);
    const [openDeleteModal, setopenDeleteModal] = React.useState(false);
    const [selectedObjects, setSelectedObjects] = React.useState([]);
    const [errorsClick, setErrorsClick] = React.useState(defaultclickErrors);
    const [sortDirection, setSortDirection] = React.useState('ASC');

    const [quotas, quotasloading, syncQuota] = QuotasProductModel.useApiModel(
        {
            organizationId: organization.id,
            productId,
            allow_cache: false,
            cache: false
        },
        [organization, productId]
    );

    const shouldSyncProduct = React.useMemo(() => {
        return variants?.filter(variant => !variant.disabled).length === 1;
    }, [variants]);

    const shouldDisplayDisableProductMessage = React.useMemo(() => {
        return variants?.every(variant => variant.disabled);
    }, [variants]);

    const productLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);

    const variantsNamesTranslations = React.useMemo(() => {
        const lang = i18n.accountLocale.toLowerCase().replace('_', '-');
        //for each variant we either use the translations if present or the name (pipetranslate)
        return variants.map(v => {
            const _translations = v.translations?.name;
            if (_translations) {
                if (_translations[lang]) {
                    v.name = _translations[lang];
                    return v;
                }
                //we tolerate alternative translations similar to account language (like fr-fr for fr-ca)
                for (const trylang of Object.keys(_translations)) {
                    if (trylang.includes(lang.split('-')[0])) {
                        v.name = _translations[trylang];
                        return v;
                    }
                }
            }
            v.name = setTranslate(v.name, lang, productLanguages);
            return v;
        });
    }, [variants, i18n, setTranslate, productLanguages]);

    const handleSave = React.useCallback(
        (value, newVariant) => {
            setSubmitting(true);
            return new VariantModel(value)
                .save({
                    organizationId: organization.id,
                    productId
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.cross-selling.listVariant.variantSaveError'));
                })
                .finally(() => {
                    toast.success(t('ticket.cross-selling.listVariant.variantAdded'));
                    setSubmitting(false);
                    resetmodal();
                    syncVariants({ allow_cache: false, cache: false });
                    syncQuota();
                    if (!newVariant) {
                        setOpenModal(false);
                    }
                });
        },
        [organization, productId, resetmodal, syncVariants, syncQuota]
    );

    const handleEdit = React.useCallback(
        (value, id, newVariant) => {
            setSubmitting(true);
            return new VariantModel({ id, ...value })
                .save({
                    organizationId: organization.id,
                    productId,
                    id
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.cross-selling.listVariant.variantEditError'));
                })
                .finally(() => {
                    toast.success(t('ticket.cross-selling.listVariant.variantEdited'));
                    resetmodal();
                    syncVariants({ allow_cache: false, cache: false });
                    syncQuota();
                    // Menhir disable product when all variants are disabled
                    if (shouldSyncProduct && value.disabled) {
                        syncProduct();
                    }
                    if (!newVariant) {
                        setOpenModal(false);
                    }
                    setSubmitting(false);
                });
        },
        [organization, productId, resetmodal, syncVariants, syncQuota, syncProduct, shouldSyncProduct]
    );

    const setVariantForModal = React.useCallback(
        object => {
            setFormObject({
                ...modalForm,
                quota: quotas?.find(q => q.variant === object.id)?.quota_total,
                name: object.name,
                id: object.id,
                price: object.price,
                disabled: object.visibility,
                reference: object.reference,
                ordering: object.ordering,
                translations: { ...(object.translations || modalForm.translations) }
            });
            setOpenModal(true);
        },
        [setFormObject, quotas]
    );

    const onVariantSelected = React.useCallback(
        (obj, event) => {
            let ret = [...selectedObjects];
            if (event.target.checked) {
                ret.push(obj.id);
            } else {
                ret = ret.filter(r => obj.id !== r);
            }
            setSelectedObjects(ret);
        },
        [selectedObjects]
    );

    const handleSelectAll = React.useCallback(() => {
        if (!selectedObjects.length) {
            return setSelectedObjects(variants.filter(v => !v.disabled).map(v => v.id));
        }
        return setSelectedObjects([]);
    }, [variants, selectedObjects]);

    const variantCanBeDeleted = React.useMemo(() => {
        return Boolean(variants?.length > 1);
    }, [variants]);

    const filteredVariants = React.useMemo(() => {
        return variants?.filter(item => selectedObjects.includes(item.id) && item.transaction_total_count < 1);
    }, [variants, selectedObjects]);

    const onDelete = React.useCallback(() => {
        let variantsToDelete = filteredVariants.map(va => {
            return { ...va.object, quota: quotas?.find(q => q.variant === va.id)?.quota_total || Infinity };
        });
        setSubmitting(true);
        fetcher
            .delete(`/ticket/organizations/${organization.id}/products/${productId}/variants`, { data: variantsToDelete })
            .then(() => {
                toast.success(t('ticket.cross-selling.listVariant.variantRemoved', { count: selectedObjects?.length }));
            })
            .catch(e => {
                toast.error(t('ticket.cross-selling.listVariant.variantRemovedError', { count: selectedObjects?.length }));
            })
            .finally(() => {
                syncVariants({ allow_cache: false, cache: false });
                syncQuota();
                setSubmitting(false);
                setopenDeleteModal(false);
                setSelectedObjects([]);
            });
    }, [productId, filteredVariants, syncVariants, syncQuota, modalForm, organization, selectedObjects]);

    const handleClose = React.useCallback(() => {
        setOpenModal(false);
        resetmodal();
    }, [resetmodal]);

    const handleSorting = ({ column, direction }) => {
        setSortDirection(direction);
    };

    const sortedVariants = React.useMemo(() => {
        if (!variantsNamesTranslations) return [];

        return [...variantsNamesTranslations].sort((a, b) => {
            if (sortDirection === 'ASC') {
                return a.ordering - b.ordering;
            } else {
                return b.ordering - a.ordering;
            }
        });
    }, [sortDirection, variantsNamesTranslations]);

    const columns = [
        {
            key: 'ordering',
            label: t('ticket.cross-selling.listVariant.Variantordering'),
            sortable: true,
            sortingDirection: sortDirection,
            render: object => <>{object.ordering + 1}</>
        },

        {
            key: 'name',
            label: t('ticket.cross-selling.listVariant.name'),
            render: object => (
                <>
                    {object.name}
                    {object.reference ? <div className={Style['column-variant-ref']}>{`${object.reference}`}</div> : null}
                </>
            )
        },
        {
            key: 'sell',
            label: t('ticket.cross-selling.listVariant.sell'),
            render: object => object.transaction_total_count
        },
        {
            key: 'quota',
            label: t('ticket.cross-selling.listVariant.quota'),
            render: object => {
                return `${quotas?.find(q => q.variant === object.id)?.quota_total || '-'}`;
            }
        },
        {
            key: 'price',
            label: t('ticket.cross-selling.listVariant.price'),
            render: object => `${object.price} ${t(`currencies.label.${organization.currency?.toLowerCase()}`)}`
        },
        {
            key: 'disabled',
            label: t('ticket.cross-selling.listVariant.disabled'),
            render: object => (
                <div onClick={e => e.stopPropagation()}>
                    <Toggle
                        checked={!object.disabled}
                        onChange={() => {
                            handleEdit(
                                {
                                    id: object.id,
                                    disabled: !object.disabled,
                                    //TODO: find a better way on Pyvar side, if quota isn't sent here WeezProductVariantSerializer send None to menhir
                                    quota: quotas?.find(q => q.variant === object.id)?.quota_total || null
                                },
                                object.id
                            );
                        }}
                    />
                </div>
            )
        },
        {
            key: 'Actions',
            label: t('ticket.cross-selling.listVariant.actions'),
            render: object => (
                <TableCellDropdown>
                    <DropdownItem
                        item={t('ticket.common.action.edit')}
                        onClick={() => {
                            setVariantForModal(object);
                        }}
                    />
                    <DropdownLine />
                    <DropdownItem
                        item={t('ticket.common.action.delete')}
                        disabled={object.transaction_total_count > 0}
                        deleteItem
                        onClick={() => {
                            setSelectedObjects([object.id]);
                            setopenDeleteModal(true);
                        }}
                    />
                </TableCellDropdown>
            )
        }
    ];

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title title={t('ticket.cross-selling.listVariant.variantItem')} />
                    <Card.SubTitle title={t('ticket.cross-selling.listVariant.addVariant')} />
                </Card.Header>
                {!variants?.length ? (
                    <Card.Content>
                        <EmptyState
                            icon={<Icon name="list" />}
                            title={t('ticket.cross-selling.listVariant.noVariant')}
                            labelButton={t('ticket.common.action.create')}
                            onClick={() => setOpenModal(true)}
                            subtitle={t('ticket.cross-selling.listVariant.creatVariant')}
                        />
                    </Card.Content>
                ) : (
                    <Card.Content>
                        <LoadingCard loading={loadingVariants || quotasloading || submitting}>
                            <div className={Style['add-cross-variant-container']}>
                                <SelectButton
                                    tooltipTitle={t('ticket.cross-selling.listVariant.noActionTooltip')}
                                    title={
                                        Object.values(selectedObjects).length > 0
                                            ? t('ticket.cross-selling.listVariant.variantAction', { count: Object.values(selectedObjects).length })
                                            : t('ticket.common.action.bulk')
                                    }
                                    objects={selectedObjects}
                                >
                                    <DropdownItem
                                        deleteItem
                                        disabled={!variantCanBeDeleted}
                                        item={t('ticket.common.action.delete')}
                                        onClick={() => {
                                            setopenDeleteModal(true);
                                        }}
                                    />
                                </SelectButton>
                                <div
                                    className={Style['add-cross-variant-add-container']}
                                    onClick={() => {
                                        resetmodal();
                                        setOpenModal(true);
                                    }}
                                >
                                    <div className={Style['add-cross-variant-add']}>{t('ticket.cross-selling.listVariant.addVariant2')}</div>
                                    <Icon name="plus" />
                                </div>
                            </div>
                            {shouldDisplayDisableProductMessage && (
                                <Message title={t('ticket.cross-selling.listVariant.productNotVisible')} type="warning">
                                    {t('ticket.cross-selling.listVariant.productDisable')}
                                </Message>
                            )}
                            <AutoTable
                                pk="id"
                                useNew
                                selectable
                                objects={sortedVariants}
                                columns={columns}
                                selectedObjects={selectedObjects}
                                onObjectSelected={onVariantSelected}
                                onObjectClicked={setVariantForModal}
                                onSelectAll={handleSelectAll}
                                onClickSorting={handleSorting}
                            />
                        </LoadingCard>
                    </Card.Content>
                )}
            </Card>

            {openModal && (
                <VariantModal
                    form={modalForm}
                    open={openModal}
                    setForm={setForm}
                    quotas={quotas}
                    variants={variants}
                    submitting={submitting}
                    handleSave={handleSave}
                    handleEdit={handleEdit}
                    errorsClick={errorsClick}
                    setFormObject={setFormObject}
                    setErrorsClick={setErrorsClick}
                    onClose={handleClose}
                    productCategory={product?.category}
                />
            )}

            <Modal
                size={'medium'}
                open={openDeleteModal}
                allowExternalClosing={false}
                onClose={() => setopenDeleteModal(false)}
                title={t('ticket.cross-selling.listVariant.confirmDeleteVariant', { count: selectedObjects.length })}
                submitButton={<Button primary onClick={() => onDelete()} label={t('ticket.common.action.confirm')} />}
                cancelButton={<Button onClick={() => setopenDeleteModal(false)} label={t('ticket.common.action.cancel')} />}
            >
                {selectedObjects.length > filteredVariants.length && (
                    <Message type="default" title={t('ticket.cross-selling.listVariant.confirmDeleteProductVariant_message_title_filtered')}>
                        {t('ticket.cross-selling.listVariant.confirmDeleteProductVariant_message_body_filtered')}
                    </Message>
                )}
                {t('ticket.cross-selling.listVariant.confirmDeleteProductVariantQuestion', { count: selectedObjects.length })}
            </Modal>
        </>
    );
}
